import { useId, useState, type ElementType } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
} from "@heroui/react";
import { ChevronDown, Download, Repeat2 } from "lucide-react";
import { FileUpload } from "@/components/ui/file-upload";

export type ActionsCardDownloadItem = {
  key: string;
  label: string;
  hint?: string;
  icon: ElementType;
  badge?: string;
  iconClassName?: string;
  onClick: () => Promise<void> | void;
  isLoading?: boolean;
};

type ActionsCardUploadConfig = {
  title: string;
  subtitle?: string;
  files: File[];
  setFiles: (files: File[]) => void;
  setError: (error: string | null) => void;
  error?: string | null;
  onSubmit: () => Promise<void> | void;
  isLoading?: boolean;
  maxFiles?: number;
  acceptedTypes?: string[];
  hideStatusChips?: boolean;
  submitLabel?: string;
  clearLabel?: string;
};

type ActionsCardDownloadsConfig = {
  title: string;
  subtitle?: string;
  items: ActionsCardDownloadItem[];
};

type ActionsCardProps = {
  title?: string;
  primaryActions?: React.ReactNode;
  upload?: ActionsCardUploadConfig;
  downloads?: ActionsCardDownloadsConfig;
};

export function ActionsCard({
  title = "Actions",
  primaryActions,
  upload,
  downloads,
}: ActionsCardProps) {
  const [openPanel, setOpenPanel] = useState<"upload" | "download" | null>(
    null,
  );
  const uploadPanelId = useId();
  const downloadPanelId = useId();

  return (
    <Card
      shadow="none"
      className="w-80 rounded-2xl bg-white ring-1 ring-neutral-200 shadow-[0_6px_20px_rgba(16,24,40,0.04)] overflow-hidden"
    >
      <CardHeader className="px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </CardHeader>

      <CardBody className="flex flex-col gap-4 px-4 pb-5 pt-0">
        {primaryActions && <div className="flex gap-2">{primaryActions}</div>}

        {primaryActions && (upload || downloads) && (
          <Divider className="my-1" />
        )}

        {upload && (
          <section aria-labelledby="upload-section">
            <button
              id="upload-section"
              type="button"
              onClick={() =>
                setOpenPanel((p) => (p === "upload" ? null : "upload"))
              }
              aria-expanded={openPanel === "upload"}
              aria-controls={uploadPanelId}
              className="group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left transition hover:bg-gray-50"
            >
              <span className="inline-flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100">
                  <Repeat2
                    size={16}
                    className="text-gray-600 group-hover:text-gray-700"
                  />
                </span>
                <span>
                  <span className="block text-[13px] font-medium text-gray-900">
                    {upload.title}
                  </span>
                  {upload.subtitle && (
                    <span className="mt-0.5 block text-[11px] text-gray-500">
                      {upload.subtitle}
                    </span>
                  )}
                </span>
              </span>
              <ChevronDown
                size={16}
                className={`ml-2 text-gray-500 transition-transform ${
                  openPanel === "upload" ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              id={uploadPanelId}
              className={`px-2.5 pt-1 overflow-hidden transition-[max-height,opacity] duration-350 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                openPanel === "upload"
                  ? "max-h-[520px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              aria-hidden={openPanel !== "upload"}
            >
              <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-2">
                <FileUpload
                  files={upload.files}
                  setFiles={upload.setFiles}
                  setError={upload.setError}
                  maxFiles={upload.maxFiles}
                  acceptedTypes={upload.acceptedTypes}
                  hideStatusChips={upload.hideStatusChips}
                />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="light"
                  radius="lg"
                  size="sm"
                  onPress={() => {
                    upload.setFiles([]);
                    upload.setError(null);
                  }}
                >
                  {upload.clearLabel ?? "Clear"}
                </Button>
                <Button
                  radius="lg"
                  size="sm"
                  className="px-3 bg-primary text-white hover:opacity-90"
                  onPress={upload.onSubmit}
                  isDisabled={
                    upload.isLoading ||
                    (upload.maxFiles
                      ? upload.files.length !== upload.maxFiles
                      : upload.files.length === 0)
                  }
                  isLoading={upload.isLoading}
                >
                  {upload.submitLabel ?? "Upload & reload"}
                </Button>
              </div>
              {upload.error && (
                <p className="mt-2 text-xs text-red-500">{upload.error}</p>
              )}
            </div>
          </section>
        )}

        {upload && downloads && <Divider className="my-1" />}

        {downloads && (
          <section aria-labelledby="download-section">
            <button
              id="download-section"
              type="button"
              onClick={() =>
                setOpenPanel((p) => (p === "download" ? null : "download"))
              }
              aria-expanded={openPanel === "download"}
              aria-controls={downloadPanelId}
              className="group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left transition hover:bg-gray-50"
            >
              <span className="inline-flex items-center gap-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100">
                  <Download
                    size={16}
                    className="text-gray-600 group-hover:text-gray-700"
                  />
                </span>
                <span>
                  <span className="block text-[13px] font-medium text-gray-900">
                    {downloads.title}
                  </span>
                  {downloads.subtitle && (
                    <span className="mt-0.5 block text-[11px] text-gray-500">
                      {downloads.subtitle}
                    </span>
                  )}
                </span>
              </span>
              <ChevronDown
                size={16}
                className={`ml-2 text-gray-500 transition-transform ${
                  openPanel === "download" ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              id={downloadPanelId}
              className={`px-1 pb-1 overflow-hidden transition-[max-height,opacity] duration-350 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                openPanel === "download"
                  ? "max-h-[520px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              aria-hidden={openPanel !== "download"}
            >
              <ul role="list" className="divide-y divide-neutral-200">
                {downloads.items.map(
                  ({
                    key,
                    label,
                    hint,
                    icon: Icon,
                    badge,
                    iconClassName,
                    onClick,
                    isLoading,
                  }) => (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={onClick}
                        disabled={isLoading}
                        className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-gray-50 disabled:opacity-60"
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white ring-1 ring-neutral-200">
                          <Icon
                            className={`h-4 w-4 ${iconClassName ?? "text-stone-500"}`}
                          />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="grid grid-cols-[minmax(0,1fr)_2.5rem] items-center gap-2 min-h-5">
                            <span className="text-[13px] font-medium text-gray-900">
                              {label}
                            </span>
                            {badge && (
                              <Chip
                                size="sm"
                                className="justify-self-end bg-gray-100 text-gray-600 text-[10px]"
                              >
                                {badge}
                              </Chip>
                            )}
                            {!badge && (
                              <span
                                aria-hidden="true"
                                className="inline-block h-5 w-10 justify-self-end opacity-0"
                              />
                            )}
                          </div>
                          {hint && (
                            <div className="truncate text-[11px] text-gray-500">
                              {hint}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </section>
        )}
      </CardBody>
    </Card>
  );
}
